import { useNavigate } from "react-router-dom";
import SubPages from "../pages/subpages/SubPagesEnum";

/**
 * Custom hook for app navigation that replaces the legacy go_to_page function
 * Maps SubPages enum to React Router routes with optional entity ID support
 */
export function useAppNavigation() {
    const navigate = useNavigate();

    const routeMap = {
        [SubPages.MyModels]: '/app/models',
        [SubPages.NewModel]: '/app/models/new',
        [SubPages.FinancialModelEditor]: '/app/models/editor/financial',
        [SubPages.OperationalModelEditor]: '/app/models/editor/operational',
        [SubPages.FinancialStatements]: '/app/financial-statements',
        [SubPages.Dashboard]: '/app/dashboard',
        [SubPages.Audit]: '/app/audit',
        [SubPages.History]: '/app/history',
        [SubPages.Tools]: '/app/tools',
        [SubPages.Compare]: '/app/compare',
        [SubPages.CalculationResults]: '/app/calculation-results',
        [SubPages.Organization]: '/app/organization',
        [SubPages.Settings]: '/app/settings',
        [SubPages.MyAccount]: '/app/account',
        [SubPages.Contact]: '/app/contact',
        [SubPages.AI]: '/app/ai',
        [SubPages.Library]: '/app/library',
        [SubPages.Portfolio]: '/app/portfolio',
        // [SubPages.Home]: '/app/',
    };

    const navigateToPage = (page: SubPages | string, entity?: any) => {
        // Handle string keys that might be passed
        const pageKey = typeof page === 'string' ? SubPages[page] : page;
        
        if (!pageKey && typeof page === 'string') {
            // If it's a string but not in enum, try to use it directly
            console.warn(`Page "${page}" not found in SubPages enum`);
            navigate(`/app/${page.toLowerCase()}`, { state: { entity } });
            return;
        }

        let route = routeMap[pageKey] || '/app/models';
        
        // Add entity ID to URL for routes that support it
        if (entity && entity.id) {
            const routesWithEntityId = [
                SubPages.FinancialModelEditor,
                SubPages.OperationalModelEditor,
                SubPages.FinancialStatements,
                SubPages.Dashboard,
                SubPages.Audit,
                SubPages.History,
                SubPages.Tools,
                SubPages.Compare,
                SubPages.CalculationResults,
                SubPages.Organization,
                SubPages.Settings,
                SubPages.MyAccount,
                SubPages.NewModel,
                SubPages.AI
            ];

            if (routesWithEntityId.includes(pageKey)) {
                route = `${route}/${entity.id}`;
            }
        }
        
        navigate(route, { state: { entity } });
    };

    return navigateToPage;
}

/**
 * Helper function to get route from SubPages enum
 * Useful for navigation guards or conditional routing
 */
export function getRouteFromPage(page: SubPages, entityId?: string | number): string {
    const routeMap = {
        [SubPages.MyModels]: '/app/models',
        [SubPages.NewModel]: '/app/models/new',
        [SubPages.FinancialModelEditor]: '/app/models/editor/financial',
        [SubPages.OperationalModelEditor]: '/app/models/editor/operational',
        [SubPages.FinancialStatements]: '/app/financial-statements',
        [SubPages.Dashboard]: '/app/dashboard',
        [SubPages.Audit]: '/app/audit',
        [SubPages.History]: '/app/history',
        [SubPages.Tools]: '/app/tools',
        [SubPages.Compare]: '/app/compare',
        [SubPages.CalculationResults]: '/app/calculation-results',
        [SubPages.Organization]: '/app/organization',
        [SubPages.Settings]: '/app/settings',
        [SubPages.MyAccount]: '/app/account',
        [SubPages.Contact]: '/app/contact',
        [SubPages.AI]: '/app/ai',
        [SubPages.Library]: '/app/library',
        [SubPages.Portfolio]: '/app/portfolio',
    };

    let route = routeMap[page] || '/app/models';

    // Add entity ID if provided and route supports it
    if (entityId) {
        const routesWithEntityId = [
            SubPages.FinancialModelEditor,
            SubPages.OperationalModelEditor,
            SubPages.Dashboard,
            SubPages.Compare,
            SubPages.CalculationResults,
            SubPages.Organization,
            SubPages.Settings,
            SubPages.MyAccount,
            SubPages.NewModel,
            SubPages.AI
        ];

        if (routesWithEntityId.includes(page)) {
            route = `${route}/${entityId}`;
        }
    }
    
    return route;
}
